import React, { useCallback, useEffect, useRef } from 'react'
import {
  selectCrossClientCurrentTabLoaded,
  selectCrossClientPaging,
  selectCrossClientResultsExecution,
  selectCrossClientResultsPollingEnabled,
  selectCrossClientSelectedTab,
  selectShouldPollCrossClientExecutionResults,
  setCrossClientMatchDistribution,
  setCrossClientPageResults,
  setCrossClientProgressStatuses,
  setCrossClientResultsExecution,
  startCrossClientExecutionPolling,
  stopCrossClientExecutionPolling,
} from '../../../store/crossClient/crossClientResults'
import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  useLazyGetCrossClientMatchDistributionQuery,
  useLazyGetCrossClientQueryExecutionProgressQuery,
  useLazyGetCrossClientQueryExecutionQuery,
  useLazyGetCrossClientQueryExecutionResultsQuery,
} from '../../../store/crossClient/crossClient'
import {
  CROSS_CLIENT_INCOMPLETE_TAB,
  CrossClientQueryResultMatch,
  CrossClientQueryResultStatus,
  CrossClientQueryResultStatuses,
  CrossClientResultTab,
} from '../../../models/CrossClient'

const EXECUTION_POLL_INTERVAL_MS = 10000
const RESULTS_POLL_INTERVAL_MS = 4000

const tabToResultFilter = (tab: CrossClientResultTab): { match?: CrossClientQueryResultMatch[]; status?: CrossClientQueryResultStatus } => {
  if (tab === CROSS_CLIENT_INCOMPLETE_TAB) {
    return { status: CrossClientQueryResultStatuses.NO_DATA }
  }
  return { match: [tab] }
}

export interface CrossClientResultsPollerProps {
  executionId: string
}

export const CrossClientResultsPoller: React.FC<CrossClientResultsPollerProps> = ({ executionId }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const shouldPoll = useAppSelector(selectShouldPollCrossClientExecutionResults)
  const pollingEnabled = useAppSelector(selectCrossClientResultsPollingEnabled)
  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const paging = useAppSelector(selectCrossClientPaging)
  const tabLoaded = useAppSelector(selectCrossClientCurrentTabLoaded)
  const executionPhase = useAppSelector(selectCrossClientResultsExecution)?.phase

  const [triggerGetCrossClientQueryExecution] = useLazyGetCrossClientQueryExecutionQuery()
  const [triggerGetResults] = useLazyGetCrossClientQueryExecutionResultsQuery()
  const [triggerGetMatchDistribution] = useLazyGetCrossClientMatchDistributionQuery()
  const [triggerGetProgress] = useLazyGetCrossClientQueryExecutionProgressQuery()

  const lastUpdatedAtRef = useRef<string | null>(null)

  useEffect(() => {
    if (shouldPoll) {
      dispatch(startCrossClientExecutionPolling())
    } else {
      dispatch(stopCrossClientExecutionPolling())
    }
  }, [dispatch, shouldPoll])

  const getCrossClientExecution = useCallback(async () => {
    try {
      const execution = await triggerGetCrossClientQueryExecution({ accountId, executionId }).unwrap()
      if (!execution) {
        dispatch(stopCrossClientExecutionPolling())
        return
      }
      dispatch(setCrossClientResultsExecution(execution))
    } catch (e) {
      console.error(e)
      dispatch(stopCrossClientExecutionPolling())
    }
  }, [dispatch, accountId, executionId, triggerGetCrossClientQueryExecution])

  const refreshCounts = useCallback(async (): Promise<string | null> => {
    try {
      const [distribution, progress] = await Promise.all([
        triggerGetMatchDistribution({ accountId, executionId }).unwrap(),
        triggerGetProgress({ accountId, executionId }).unwrap(),
      ])
      if (distribution) {
        dispatch(setCrossClientMatchDistribution(distribution))
      }
      if (progress) {
        dispatch(setCrossClientProgressStatuses(progress.statuses))
      }
      return progress?.lastUpdatedAt ?? null
    } catch (e) {
      console.error(e)
      return null
    }
  }, [dispatch, accountId, executionId, triggerGetMatchDistribution, triggerGetProgress])

  const refreshPage = useCallback(async () => {
    const tab = selectedTab
    const pageIndex = Math.max(paging.page - 1, 0)
    try {
      const { match, status } = tabToResultFilter(tab)
      const response = await triggerGetResults({
        accountId,
        executionId,
        match,
        status,
        page: pageIndex,
        size: paging.size,
      }).unwrap()
      dispatch(setCrossClientPageResults({ tab, response }))
    } catch (e) {
      console.error(e)
    }
  }, [dispatch, accountId, executionId, selectedTab, paging.page, paging.size, triggerGetResults])

  useEffect(() => {
    if (!tabLoaded) {
      void refreshPage()
    }
  }, [tabLoaded, refreshPage])

  const previousPhaseRef = useRef(executionPhase)
  useEffect(() => {
    if (previousPhaseRef.current !== executionPhase) {
      previousPhaseRef.current = executionPhase
      void refreshPage()
    }
  }, [executionPhase, refreshPage])

  useEffect(() => {
    void refreshCounts()
  }, [refreshCounts, executionPhase])

  const pollResults = useCallback(async () => {
    const latest = await refreshCounts()
    if (latest !== lastUpdatedAtRef.current) {
      lastUpdatedAtRef.current = latest
      await refreshPage()
    }
  }, [refreshCounts, refreshPage])

  const { start: startExecutionPolling, stop: stopExecutionPolling } = usePollingEffect(getCrossClientExecution, [], {
    interval: EXECUTION_POLL_INTERVAL_MS,
  })

  const { start: startResultsPolling, stop: stopResultsPolling } = usePollingEffect(pollResults, [], {
    interval: RESULTS_POLL_INTERVAL_MS,
  })

  useEffect(() => {
    if (pollingEnabled) {
      startExecutionPolling()
      startResultsPolling()
    } else {
      stopExecutionPolling()
      stopResultsPolling()
    }
  }, [pollingEnabled, startExecutionPolling, startResultsPolling, stopExecutionPolling, stopResultsPolling])

  return null
}
